import { CabinetType, CircuitType } from '@/api/circuit';
import {
  V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse,
  V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { nodeSizeMap, nodeSizeMapByString } from './registerG6';
import { keepTwoOrLine } from '@/utils/utils';

export function getNodeSize(type: string) {
  return nodeSizeMap[type!] || [0, 0];
}

// 判断是否画配电柜（母排）
const judgeHasRedLine = (type?: string) => {
  if (type !== 'gridBilling' && type !== 'transformer' && type !== 'lineGate') {
    return true;
  }
  return false;
};

export const findTreeNodeBySourceId = (
  tree: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree'],
  sourceId?: number
) => {
  const list = generateList(tree);
  const findTreeNode = list.find(i => i.id === sourceId);
  return findTreeNode;
};

export function convertHighDataToGraph(
  flatList: {
    id: number;
    type: string;
    value: any;
  }[],
  dataProperty?: V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse,
  data?: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse
) {
  const nodes: any = [];

  (data?.list ?? []).forEach((group, lineIndex) => {
    const firstNode = group.primaryWiringDiagramInfo?.[0].next?.[0];
    const [firstChildWidth, _] = getNodeSize(formatLowType(firstNode?.type!));
    let itemGap = firstChildWidth / 2; // 间距
    let positionTemp = 'left'; // 进线回路上级是引闸刀
    (group.primaryWiringDiagramInfo ?? []).forEach((cabinet, cabinetIndex) => {
      const cabX = itemGap;

      // 获取当前节点的宽度（计算下一个节点的坐标位置）
      const firstNode = cabinet.next?.[0];
      const findTreeNode = findTreeNodeBySourceId(data?.tree, firstNode?.value?.sourceId);
      const [firstChildWidth, _] = getNodeSize(
        formatType(firstNode?.type!, firstNode?.value?.cabinetType, findTreeNode)
      );
      const secondNode = group.primaryWiringDiagramInfo?.[cabinetIndex + 1]?.next?.[0];
      const findSecondTreeNode = findTreeNodeBySourceId(data?.tree, secondNode?.value?.sourceId);
      const [secondChildWidth, __] = getNodeSize(
        formatType(secondNode?.type!, secondNode?.value?.cabinetType, findSecondTreeNode)
      );
      // 子节点
      if (cabinet.next && cabinet.next.length > 0) {
        cabinet.next.forEach((child: any, childIndex: number) => {
          const findTreeNode = findTreeNodeBySourceId(data?.tree, child?.value?.sourceId);

          const [childWidth, childHeight] = getNodeSize(
            formatType(child.type, child?.value?.cabinetType, findTreeNode)
          );
          const childX = cabX;

          const onlyKey = `${lineIndex}-1`;

          // 画上游回路（电网计费，变压器，引闸刀）// 这里的sourceId需要去重（不然多个需要画多遍）
          if (findTreeNode) {
            drawUpLoop(child, nodes, childX, onlyKey, data, dataProperty);
          }

          // 引自与引至
          const toCircuitItem = flatList?.find(i => i?.value?.fromCircuitId === child?.id);
          const toCircuitName = toCircuitItem ? '引至：' + toCircuitItem?.value?.name : '';
          const fromCircuitName = child?.value?.fromCircuitId
            ? '引自：' + flatList?.find(i => i?.id === child?.value?.fromCircuitId)?.value?.name
            : '';

          nodes.push({
            id: child.id.toString(),
            lineIndex: onlyKey,
            label: child?.value?.name ?? '--',
            x: childX,
            y: childHeight / 2,
            size: [childWidth, childHeight],
            // 如果配电柜下面不是电网计费或者变压器或者压变，则有配电柜（母排）得画红线
            hasRedLine: judgeHasRedLine(cabinet.next?.[0].type) && childIndex === 0,
            // 暂时处理进线回路的配电柜不同的方式
            type: formatType(child.type, child?.value?.cabinetType, findTreeNode),
            anchorPoints: [
              [1, 0], // 右上角
              [0, 0], // 左上角
            ],
            // 同一行，用来连线（不然会连接到垂直的父节点上）
            isOneLine: true,
            isHigh: true,
            isLastNode: cabinetIndex === (group?.primaryWiringDiagramInfo ?? []).length - 1,
            value: {
              ...child?.value,
              currentA: dataProperty?.list?.find(i => i.circuitId === child.id)?.currentA,
              currentB: dataProperty?.list?.find(i => i.circuitId === child.id)?.currentB,
              currentC: dataProperty?.list?.find(i => i.circuitId === child.id)?.currentC,
              voltageA: dataProperty?.list?.find(i => i.circuitId === child.id)?.voltageA,
              voltageB: dataProperty?.list?.find(i => i.circuitId === child.id)?.voltageB,
              voltageC: dataProperty?.list?.find(i => i.circuitId === child.id)?.voltageC,
              cabinetName: cabinet.value?.name,
              toCircuitName: toCircuitName,
              fromCircuitName: fromCircuitName,
              position:
                child.type === 'inComing' && findTreeNode?.type === CircuitType.LEAD_WIRE_SWITCH_CIRCUIT
                  ? positionTemp
                  : undefined,
            },
          });
          if (child.type === 'inComing' && findTreeNode?.type === CircuitType.LEAD_WIRE_SWITCH_CIRCUIT) {
            positionTemp = 'right';
          }
        });
      }
      // 递归下一个起始x
      itemGap = itemGap + firstChildWidth / 2 + secondChildWidth / 2;
    });
  });

  return { nodes };
}

// 生成节点映射表（树形结构-->平铺）
const generateList = (data: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree']) => {
  let tempDataList: { id: number; type: number; value: any }[] = [];
  const loopData = (data: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree']) => {
    for (let i = 0; i < (data ?? []).length; i++) {
      const node = data?.[i];
      tempDataList.push({ id: node?.id!, type: node?.type!, value: node ?? '' });
      if (node?.children) {
        loopData(node.children);
      }
    }
  };
  loopData(data);
  return tempDataList;
};

// 通过子节点找到父节点
function findParentNode(
  tree: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree'],
  childId: number | string
): any {
  let parentNode = null;

  function traverse(nodes: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree'] = [], parent: any) {
    for (const node of nodes) {
      if (node.id === childId) {
        parentNode = parent ? { ...parent, value: parent } : null;
        return; // 找到就停止
      }
      if (node.children) {
        traverse(node.children, node);
      }
    }
  }
  traverse(tree, null);
  return parentNode;
}

// 三种类型是竖着的
const judgeVertical = (type?: number, Ptype?: number) => {
  // 电网计费回路、变压器回路两者相邻，则上下结构；变压器回路、引闸刀回路两者相邻，则上下结构；
  // 变压器回路、进线回路两者相邻，则上下结构；环网柜的出线回路的下级回路是变压器回路时，是上下结构，变压器回路接在出线回路（环网柜）下
  if (type === CircuitType.INCOMING_CIRCUIT && Ptype === CircuitType.TRANSFORMER_CIRCUIT) {
    return true;
  }
  if (
    type === CircuitType.GRID_BILLING_CIRCUIT ||
    type === CircuitType.TRANSFORMER_CIRCUIT ||
    type === CircuitType.LEAD_WIRE_SWITCH_CIRCUIT
  ) {
    return true;
  }
  return false;
};

// 轮巡向上画图
const drawUpLoop = (
  child: any,
  nodes: any[],
  childX: number,
  lineIndex: string,
  data?: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse,
  dataProperty?: V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse
) => {
  const findTreeNode = findTreeNodeBySourceId(data?.tree, child?.value?.sourceId);
  // const [_, childHeight] = getNodeSize(formatType(child?.type!, child?.value?.cabinetType, findTreeNode));

  if (child.type === 'inComing' && findTreeNode && findTreeNode.type === CircuitType.LEAD_WIRE_SWITCH_CIRCUIT) {
    const extend = {
      hasExtend: true,
      extendLineType: formatType(child.type, child?.value?.cabinetType, findTreeNode),
    };
    loopDraw(
      lineIndex,
      findTreeNode,
      nodes,
      childX + nodeSizeMap[formatType(child.type, child?.value?.cabinetType, findTreeNode)][0] / 2,
      0, // 0为基线
      data,
      dataProperty,
      extend
    );
  } else {
    findTreeNode && loopDraw(lineIndex, findTreeNode, nodes, childX, 0, data, dataProperty);
  }
};

const loopDraw = (
  lineIndex: string,
  findTreeNode: { id: number; type: number; value: any },
  nodes: any[],
  childX: number,
  childY: number,
  data?: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse,
  dataProperty?: V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse,
  extend?: any
) => {
  const findPNode = findParentNode(data?.tree, findTreeNode?.id);
  // 如果还有父级，则往上再画
  if (findTreeNode) {
    const [PWidth, PHeight] = getNodeSize(
      formatType(nodeSizeMapByString[findTreeNode.type], findTreeNode?.value?.cabinetType, findPNode)
    );

    nodes.push({
      lineIndex: lineIndex,
      id: findTreeNode.id.toString(),
      label: findTreeNode.value.name,
      x: childX,
      y: childY - PHeight / 2 - 0.5, // -当前节点高度一半，-父节点高度一半， 0.5是些许偏差
      size: [PWidth, PHeight],
      type: formatType(nodeSizeMapByString[findTreeNode.type], findTreeNode?.value?.cabinetType, findPNode),
      value: {
        ...findTreeNode?.value,
        currentA: dataProperty?.list?.find(i => i.circuitId === findTreeNode.id)?.currentA,
        currentB: dataProperty?.list?.find(i => i.circuitId === findTreeNode.id)?.currentB,
        currentC: dataProperty?.list?.find(i => i.circuitId === findTreeNode.id)?.currentC,
        voltageA: dataProperty?.list?.find(i => i.circuitId === findTreeNode.id)?.voltageA,
        voltageB: dataProperty?.list?.find(i => i.circuitId === findTreeNode.id)?.voltageB,
        voltageC: dataProperty?.list?.find(i => i.circuitId === findTreeNode.id)?.voltageC,
      },
      hasRedLine: false,
      extend,
    });
    // 轮巡向上画图
    if (findPNode && judgeVertical(findTreeNode?.type, findPNode?.type)) {
      loopDraw(lineIndex, findPNode, nodes, childX, childY - PHeight, data, dataProperty);
    }
  }
};

// 判断节点类型，渲染不同节点（有些根据配电柜类型）（高压）
export const formatType = (type: any, cabinetType: any, findTreeNode: any) => {
  if (type === 'outGoing' || type === 'voltageVariable') {
    return type + (cabinetType ?? '');
  }
  if (type === 'inComing') {
    // 环网柜直接return
    if (cabinetType === CabinetType.RING_MAIN_UNIT) {
      return type + (cabinetType ?? '');
    }
    // 有父级
    if (findTreeNode) {
      return type + '_hasP' + (cabinetType ? '_' + cabinetType : '');
    }
    return type + '_noP' + (cabinetType ? '_' + cabinetType : '');
  }
  return type;
};

// 判断节点类型，渲染不同节点（有些根据配电柜类型）（低压）
const formatLowType = (type: any) => {
  return 'low_' + type;
};

// 低压画图逻辑
export function convertLowDataToGraph(
  flatList: {
    id: number;
    type: string;
    value: any;
  }[],
  dataProperty?: V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse,
  data?: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse
) {
  const nodes: any = [];

  (data?.list ?? []).forEach((group, lineIndex) => {
    const firstNode = group.lowWiringDiagramInfo?.[0].next?.[0];
    const [firstChildWidth, _] = getNodeSize(formatLowType(firstNode?.type!));
    let itemGap = firstChildWidth / 2; // 低压比较宽，往右偏移,防止连线重叠
    (group.lowWiringDiagramInfo ?? []).forEach((cabinet, cabinetIndex) => {
      let totalY = 0; // 高度偏移计算
      // 获取当前节点的宽度（计算下一个节点的坐标位置）
      const firstNode = cabinet.next?.[0];
      const [firstChildWidth, firstChildHeight] = getNodeSize(formatLowType(firstNode?.type!));
      const secondNode = group.lowWiringDiagramInfo?.[cabinetIndex + 1]?.next?.[0];
      const [secondChildWidth, __] = getNodeSize(formatLowType(secondNode?.type!));
      const cabX = itemGap;

      // 子节点
      if (cabinet.next && cabinet.next.length > 0) {
        // 高度
        totalY = firstChildHeight / 2;
        cabinet.next?.forEach((child: any, childIndex: number) => {
          const findTreeNode = findTreeNodeBySourceId(data?.tree, child?.value?.sourceId);

          const [childWidth, childHeight] = getNodeSize(formatLowType(child?.type));
          const [_, childSecondHeight] = getNodeSize(formatLowType(cabinet?.next?.[childIndex + 1]?.type));

          const childX = cabX;

          const onlyKey = `${lineIndex}-2`;

          // 画上游回路（电网计费，变压器，引闸刀）// 这里的sourceId需要去重（不然多个需要画多遍）（只会画第一个）
          if (findTreeNode) {
            drawUpLoop(child, nodes, childX, onlyKey, data, dataProperty);
          }

          // 引自与引至
          const toCircuitItem = flatList?.find(i => i?.value?.fromCircuitId === child?.id);
          const toCircuitName = toCircuitItem ? '引至：' + toCircuitItem?.value?.name : '';
          const fromCircuitName = child?.value?.fromCircuitId
            ? '引自：' + flatList?.find(i => i?.id === child?.value?.fromCircuitId)?.value?.name
            : '';

          nodes.push({
            id: child.id.toString(),
            lineIndex: onlyKey,
            label: child?.value?.name || child.id,
            x: childX,
            y: totalY,
            size: [childWidth, childHeight],
            hasRedLine: childIndex === 0,
            // 暂时处理进线回路的配电柜不同的方式
            type: formatLowType(child.type),
            anchorPoints: [
              [1, 0], // 右上角
              [0, 0], // 左上角
            ],
            // 同一行，用来连线（不然会连接到垂直的父节点上）
            isOneLine: true,
            isHigh: false,
            isLastNode: cabinetIndex === (group?.lowWiringDiagramInfo ?? []).length - 1,
            value: {
              ...child?.value,
              cabinetName: childIndex === 0 ? cabinet.value?.name : '',
              toCircuitName,
              fromCircuitName,
              currentA: dataProperty?.list?.find(i => i.circuitId === child?.id)?.currentA,
              currentB: dataProperty?.list?.find(i => i.circuitId === child?.id)?.currentB,
              currentC: dataProperty?.list?.find(i => i.circuitId === child?.id)?.currentC,
              voltageA: dataProperty?.list?.find(i => i.circuitId === child?.id)?.voltageA,
              voltageB: dataProperty?.list?.find(i => i.circuitId === child?.id)?.voltageB,
              voltageC: dataProperty?.list?.find(i => i.circuitId === child?.id)?.voltageC,
            },
          });
          totalY += childHeight / 2 + childSecondHeight / 2 - 1;
        });
      }
      // 递归下一个起始x
      itemGap = itemGap + firstChildWidth / 2 + secondChildWidth / 2;
    });
  });

  return { nodes };
}

// 根据画布宽度拆分N行
export function splitRowsByWidth(
  data: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse,
  canvasWidth: number,
  gap: number = 0
) {
  const resultHigh: any[] = [];
  const resultLow: any[] = [];

  data?.list?.forEach(group => {
    let currentHighRow: any[] = [];
    let currentLowRow: any[] = [];
    let currentHighWidth = 0;
    let currentLowWidth = 0;

    group.primaryWiringDiagramInfo?.forEach((node, index) => {
      const firstNode = node.next?.[0];
      const findTreeNode = findTreeNodeBySourceId(data.tree, firstNode?.value?.sourceId);
      const [childWidth, childHeight] = getNodeSize(
        formatType(firstNode?.type, firstNode?.value?.cabinetType, findTreeNode)
      );
      if (currentHighWidth + childWidth > canvasWidth) {
        // 超宽 → 推一行
        resultHigh.push({ primaryWiringDiagramInfo: currentHighRow });
        currentHighRow = [];
        currentHighWidth = 0;
        // 标记为换行节点（增加引自引至）
        const nextNode = group.primaryWiringDiagramInfo?.[index].next?.[0];
        (nextNode?.value as any).isNextRow = true;
      }
      currentHighRow.push(node);
      currentHighWidth += childWidth + gap;
    });
    group.lowWiringDiagramInfo?.forEach((node, index) => {
      const firstNode = node.next?.[0];
      const [childWidth, childHeight] = getNodeSize(formatLowType(firstNode?.type));
      if (currentLowWidth + childWidth > canvasWidth) {
        // 超宽 → 推一行
        resultLow.push({ lowWiringDiagramInfo: currentLowRow });
        currentLowRow = [];
        currentLowWidth = 0;
        // 标记为换行节点（增加引自引至）
        const nextNode = group.lowWiringDiagramInfo?.[index].next?.[0];
        (nextNode?.value as any).isNextRow = true;
      }
      currentLowRow.push(node);
      currentLowWidth += childWidth + gap;
    });

    if (currentHighRow.length) {
      resultHigh.push({ primaryWiringDiagramInfo: currentHighRow });
    }
    if (currentLowRow.length) {
      resultLow.push({ lowWiringDiagramInfo: currentLowRow });
    }
  });

  // 添加 fromCircuitId 只有超宽了才去加
  if (resultHigh.length > (data?.list ?? []).length) {
    for (let i = 1; i < resultHigh.length; i++) {
      if (resultHigh[i]?.primaryWiringDiagramInfo?.[0]?.next?.[0]?.value?.isNextRow) {
        const prevRow = resultHigh[i - 1].primaryWiringDiagramInfo;
        const lastNode = prevRow?.[prevRow.length - 1]?.next?.[0];
        const firstNode = resultHigh[i]?.primaryWiringDiagramInfo?.[0]?.next?.[0];
        (firstNode as any).value.fromCircuitId = lastNode?.id;
      }
    }
  }
  // 添加 fromCircuitId 只有超宽了才去加
  if (resultLow.length > (data?.list ?? []).length) {
    for (let i = 1; i < resultLow.length; i++) {
      if (resultLow[i]?.lowWiringDiagramInfo?.[0]?.next?.[0]?.value?.isNextRow) {
        const prevRow = resultLow[i - 1].lowWiringDiagramInfo;
        const lastNode = prevRow?.[prevRow.length - 1]?.next?.[0];
        const firstNode = resultLow[i]?.lowWiringDiagramInfo?.[0]?.next?.[0];
        (firstNode as any).value.fromCircuitId = lastNode?.id;
      }
    }
  }

  return [...resultHigh, ...resultLow];
}

// 处理剩下的tree节点
export const convertRestTreeToGraph = (
  nodes: any[],
  tree?: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree'],
  dataProperty?: V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse
) => {
  const restTreeData = tree?.filter(i => !nodes.map(c => Number(c.id)).includes(i.id!));
  const restAddedNodes: any[] = [];
  let itemGap = 0; // 间距
  console.log('restTreeData', restTreeData);
  restTreeData?.forEach((item, index) => {
    const [childWidth, childHeight] = getNodeSize(
      formatType(nodeSizeMapByString[item.type!], item.cabinetType, undefined)
    );
    restAddedNodes.push({
      id: item.id?.toString(),
      lineIndex: index,
      label: item?.name,
      x: itemGap,
      y: childHeight / 2,
      size: [childWidth, childHeight],
      // 暂时处理进线回路的配电柜不同的方式
      type: formatType(nodeSizeMapByString[item.type!], item.cabinetType, undefined),
      anchorPoints: [
        [1, 0], // 右上角
        [0, 0], // 左上角
      ],
      value: {
        ...item,
        currentA: dataProperty?.list?.find(i => i.circuitId === item?.id)?.currentA,
        currentB: dataProperty?.list?.find(i => i.circuitId === item?.id)?.currentB,
        currentC: dataProperty?.list?.find(i => i.circuitId === item?.id)?.currentC,
        voltageA: dataProperty?.list?.find(i => i.circuitId === item?.id)?.voltageA,
        voltageB: dataProperty?.list?.find(i => i.circuitId === item?.id)?.voltageB,
        voltageC: dataProperty?.list?.find(i => i.circuitId === item?.id)?.voltageC,
      },
    });
    if (item.children && item.children.length) {
      loopDownRestTree(item.children, item, 0, restAddedNodes, dataProperty, index);
    }
  });
  return restAddedNodes;
};

export type TreeItem = Exclude<V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['tree'], undefined>[number];

export const loopDownRestTree = (
  childrenNodes: TreeItem[],
  parentData: TreeItem,
  childY: number,
  restAddedNodes: any[],
  dataProperty?: V2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPostResponse,
  lineIndex?: number
) => {
  const [_, parentHeight] = getNodeSize(formatType(parentData?.type, parentData.cabinetType, parentData));
  childrenNodes.forEach(item => {
    const [childWidth, childHeight] = getNodeSize(
      formatType(nodeSizeMapByString[item.type!], item?.cabinetType, parentData)
    );
    const calcChildY = childY + parentHeight / 2 + childHeight / 2 - 0.5;
    restAddedNodes.push({
      id: item.id?.toString(),
      lineIndex: lineIndex,
      label: item?.name,
      x: 0,
      y: calcChildY,
      size: [childWidth, childHeight],
      // 暂时处理进线回路的配电柜不同的方式
      type: formatType(nodeSizeMapByString[item.type!], item.cabinetType, parentData),
      anchorPoints: [
        [1, 0], // 右上角
        [0, 0], // 左上角
      ],
      value: {
        ...item,
        currentA: dataProperty?.list?.find(i => i.circuitId === item?.id)?.currentA,
        currentB: dataProperty?.list?.find(i => i.circuitId === item?.id)?.currentB,
        currentC: dataProperty?.list?.find(i => i.circuitId === item?.id)?.currentC,
        voltageA: dataProperty?.list?.find(i => i.circuitId === item?.id)?.voltageA,
        voltageB: dataProperty?.list?.find(i => i.circuitId === item?.id)?.voltageB,
        voltageC: dataProperty?.list?.find(i => i.circuitId === item?.id)?.voltageC,
      },
    });
    if (item.children && item.children.length) {
      loopDownRestTree(item.children, item, calcChildY, restAddedNodes, dataProperty);
    }
  });
};
